import type { ProjectConfiguration } from "./types";

export type ChangeOrderStatus = "Draft" | "Pending Review" | "Signed" | "Voided";

export interface ChangeOrderLineItem {
  description: string;
  amount: number;
  /** Quantity of items being changed (overrides default from removed material) */
  quantity?: number;
  /** Type of material change for grouping in summary */
  actionType?: 'replace' | 'add' | 'remove_only';
}

export interface ChangeOrder {
  id: string;
  jobId: string;
  /** Sequential number within a job (CO-1, CO-2, …) */
  orderNumber: number;
  status: ChangeOrderStatus;
  /** Description of what changed */
  summary: string;
  /** Detailed change items with individual pricing */
  lineItems: ChangeOrderLineItem[];
  /** Optional revised project configuration */
  revisedConfig?: ProjectConfiguration;
  /** Contract totals (customer-facing — NEVER used as/derived into contractor pay, see canonicalSyncGuardrail.test.ts) */
  originalContractValue: number;
  changeAmount: number;
  revisedContractValue: number;
  /**
   * Amount added to the INSTALLER's payout for this change order — a
   * separate, manually-entered figure, independent of changeAmount/
   * revisedContractValue (which are customer-facing only). Defaults to 0
   * when a change order doesn't affect installer compensation (e.g. a
   * customer-facing price adjustment with no extra installer work).
   * Summed by installer-invoice-amount-compute across Signed change orders.
   */
  installerPayoutDelta?: number;
  /** Signatures */
  homeownerSignature?: string;
  homeownerSignedAt?: string;
  repSignature?: string;
  repSignedAt?: string;
  /** Tracking */
  createdAt: string;
  createdBy: string;
  signedAt?: string;
  sentToCustomerAt?: string;
  /** Notes */
  notes: string;
}

export function getNextChangeOrderNumber(existing: ChangeOrder[]): number {
  if (existing.length === 0) return 1;
  return Math.max(...existing.map(co => co.orderNumber)) + 1;
}
